import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

const written = (w) => w.action === "created" || w.action === "updated";

/** SparkPost per-site connection state + actions (status, DNS matrix, connect/fix/verify/disconnect/test). */
export function useSparkPost(site, reload) {
  const [busy, setBusy] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [status, setStatus] = useState(null);
  const [matrix, setMatrix] = useState([]);
  const connected = Boolean(site.has_sparkpost_key) || Boolean(site.sparkpost_connected_at);

  const refreshMatrix = useCallback(async () => {
    try { const r = await api.get(`/sites/${site.id}/sparkpost/dns-matrix`); setMatrix(r.data.matrix || []); setErr(""); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  }, [site.id]);

  const refreshStatus = useCallback(async () => {
    try { const r = await api.get(`/sites/${site.id}/sparkpost/status`); setStatus(r.data); setErr(""); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  }, [site.id]);

  useEffect(() => {
    if (!connected) return;
    let cancelled = false;
    (async () => { await refreshStatus(); if (!cancelled) await refreshMatrix(); })();
    return () => { cancelled = true; };
  }, [connected, refreshStatus, refreshMatrix]);

  async function run(fn, row = null) {
    setBusy(true); setBusyRow(row); setErr(""); setMsg("");
    try { await fn(); return true; }
    catch (e) { setErr(e.response?.data?.detail || e.message); return false; }
    finally { setBusy(false); setBusyRow(null); }
  }

  const connect = (body) => run(async () => {
    const r = await api.post(`/sites/${site.id}/sparkpost/connect`, body);
    setMsg(`Connected · ${(r.data.dns_writes || []).filter(written).length} DNS record(s) written · verification triggered`);
    setMatrix(r.data.dns_matrix || []);
    await reload();
    await refreshStatus();
  });

  const fixDns = () => run(async () => {
    const r = await api.post(`/sites/${site.id}/sparkpost/dns/apply`);
    const writes = r.data.writes || [];
    setMsg(`Fix DNS · wrote ${writes.filter(written).length}, skipped ${writes.filter((w) => w.action === "skip").length} already-ok`);
    setMatrix(r.data.matrix || []);
  }, "all");

  const verifyAll = () => run(async () => {
    const r = await api.post(`/sites/${site.id}/sparkpost/verify`);
    setMsg("Verification triggered · matrix refreshed");
    setMatrix(r.data.matrix || []);
    await refreshStatus();
  }, "verify");

  const disconnect = (deleteDns) => {
    if (!window.confirm(`Disconnect SparkPost${deleteDns ? " and DELETE the DKIM / bounce / tracking DNS records" : ""}?`)) return Promise.resolve(false);
    return run(async () => {
      await api.delete(`/sites/${site.id}/sparkpost/disconnect${deleteDns ? "?delete_dns=true" : ""}`);
      setMsg("Disconnected"); setStatus(null); setMatrix([]);
      await reload();
    });
  };

  const sendTest = (toEmail) => {
    if (!toEmail.trim()) { setErr("Enter a recipient email"); return Promise.resolve(false); }
    return run(async () => {
      const r = await api.post(`/sites/${site.id}/sparkpost/send-test`, { to_email: toEmail.trim() });
      setMsg(`Sent · accepted=${r.data.result?.total_accepted_recipients ?? "?"} id=${r.data.result?.id || "—"}`);
    });
  };

  return { connected, busy, busyRow, msg, err, setErr, status, matrix, refreshMatrix, connect, fixDns, verifyAll, disconnect, sendTest };
}
